import type { MouseEvent } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { AppRoutes } from '@consts/routes';
import type { Selection } from './orgNavTypes';

// Build an org-detail URL from the LIVE url (window.location), preserving the
// current params (tab + org filters) and setting type/id.
const buildOrgDetailUrl = (type: Selection | string, id: string | number): string => {
    const params = new URLSearchParams(window.location.search);
    params.set('type', String(type));
    params.set('id', String(id));
    return `${AppRoutes.Organizations}?${params.toString()}`;
};

/**
 * Returns a builder for an org-detail URL that preserves the current params.
 * Use for cards/panels that re-render and navigate at click time.
 */
export const useOrgDetailLink = () => {
    useLocation();
    return (type: Selection | string, id: string | number): string => buildOrgDetailUrl(type, id);
};

/**
 * Returns `{ to, onClick }` for a table-cell link. MUI DataGrid memoizes cell
 * output, so a precomputed `to` can go stale and drop a filter set after the
 * cell first rendered. The onClick navigates from the LIVE url at click time;
 * `to` stays as the href for accessibility and modifier-click (open in new tab).
 */
export const useOrgDetailLinkProps = () => {
    useLocation();
    const navigate = useNavigate();

    return (type: Selection | string, id: string | number) => ({
        to: buildOrgDetailUrl(type, id),
        onClick: (e: MouseEvent) => {
            // Let modifier/middle clicks fall through to the href (new tab).
            if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
            e.preventDefault();
            navigate(buildOrgDetailUrl(type, id));
        },
    });
};
